import { seoFixturesEnabled, fixtureArticle } from "../fixtures/seoFixtures";
import { supabase } from "../admin/supabase";

/**
 * Service layer for the public Blog & Articles module.
 * Reuses the existing `articles` / `categories` / `profiles` tables
 * (the same ones the CMS manages at /admin/articles) plus the new
 * `tags` / `article_tags` join table added for this module.
 *
 * No component should query Supabase directly — everything the
 * blog pages need goes through the functions in this file.
 */

const ARTICLE_COLUMNS = `
  id, slug, title, summary, body, featured_image, category_id, author_id,
  author_name, status, featured, sticky, reading_time, view_count,
  seo_title, seo_description, seo_keywords, canonical_url, og_image,
  published_at, created_at, updated_at,
  category:categories ( id, name, slug ),
  author:profiles ( id, full_name, avatar_url, bio, position, social_links ),
  article_tags ( tags ( id, name, slug ) )
`;

/** Flattens the nested { article_tags: [{ tags: {...} }] } shape into a plain `tags` array. */
function normalizeArticle(row) {
  if (!row) return null;
  const tags = (row.article_tags || [])
    .map((link) => link.tags)
    .filter(Boolean);
  const { article_tags, ...rest } = row;
  return { ...rest, tags };
}

/**
 * Fetch a paginated list of published articles.
 * filters: { categorySlug, tagSlug, page = 1, pageSize = 9 }
 * Returns { articles, total, page, pageSize, totalPages }.
 */
export async function getArticles({ categorySlug, tagSlug, page = 1, pageSize = 9 } = {}) {
  let query = supabase
    .from("articles")
    .select(ARTICLE_COLUMNS, { count: "exact" })
    .eq("status", "published")
    // Manual ordering from the CMS wins; everything left at 0 keeps
    // falling back to newest-first by publish date.
    .order("display_order", { ascending: false })
    .order("published_at", { ascending: false });

  if (categorySlug) {
    // Resolve the slug to an id first — filtering directly on the FK
    // is the reliable way to restrict the parent rows (a dot-path
    // filter on the embedded `category` resource would filter the
    // embedded object, not which articles come back).
    const { data: cat } = await supabase.from("categories").select("id").eq("slug", categorySlug).maybeSingle();
    query = query.eq("category_id", cat?.id || "00000000-0000-0000-0000-000000000000");
  }

  if (tagSlug) {
    const { data: tag } = await supabase.from("tags").select("id").eq("slug", tagSlug).maybeSingle();
    if (tag?.id) {
      const { data: links } = await supabase.from("article_tags").select("article_id").eq("tag_id", tag.id);
      const ids = (links || []).map((l) => l.article_id);
      query = query.in("id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"]);
    }
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    articles: (data || []).map(normalizeArticle),
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.max(1, Math.ceil((count || 0) / pageSize)),
  };
}

/** Fetch a single published article by its SEO-friendly slug. Returns null if not found. */
export async function getArticleBySlug(slug) {
  const { data, error } = await supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();

  if (error && !seoFixturesEnabled()) throw error;
  const article = normalizeArticle(data);
  if (!article && seoFixturesEnabled()) return fixtureArticle(slug);
  return article;
}

/** Bumps the view counter for an article via a SECURITY DEFINER RPC (anon-safe). */
export async function incrementArticleView(articleId) {
  if (!articleId) return;
  const { error } = await supabase.rpc("increment_article_view", { article_id: articleId });
  if (error) {
    // Non-fatal — view counting should never break the reading experience.
    console.error("Failed to record article view", error);
  }
}

/** Most recent featured, published article — used for the blog hero. */
export async function getFeaturedArticle() {
  const { data, error } = await supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .eq("status", "published")
    .eq("featured", true)
    .order("published_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return normalizeArticle(data);
}

/** Most-viewed published articles, for "Popular / Trending" sidebar sections. */
export async function getPopularArticles(limit = 5) {
  const { data, error } = await supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .eq("status", "published")
    .order("view_count", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(normalizeArticle);
}

/** Most recently published articles. */
export async function getRecentArticles(limit = 5, excludeId = null) {
  let query = supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(limit);

  if (excludeId) query = query.neq("id", excludeId);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(normalizeArticle);
}

/**
 * Related articles for a given article: same category OR overlapping
 * tags, excluding the current article. Returns up to `limit` results
 * (3–6 recommended by the design spec).
 */
export async function getRelatedArticles(article, limit = 4) {
  if (!article) return [];

  const tagIds = (article.tags || []).map((t) => t.id);
  let tagArticleIds = [];
  if (tagIds.length) {
    const { data: links } = await supabase
      .from("article_tags")
      .select("article_id")
      .in("tag_id", tagIds)
      .neq("article_id", article.id);
    tagArticleIds = (links || []).map((l) => l.article_id);
  }

  const orParts = [];
  if (article.category_id) orParts.push(`category_id.eq.${article.category_id}`);
  if (tagArticleIds.length) orParts.push(`id.in.(${tagArticleIds.join(",")})`);

  if (!orParts.length) return [];

  const { data, error } = await supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .eq("status", "published")
    .neq("id", article.id)
    .or(orParts.join(","))
    .order("published_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(normalizeArticle);
}

/** The published article immediately before/after this one by publish date, for prev/next navigation. */
export async function getAdjacentArticles(publishedAt) {
  const [{ data: prev }, { data: next }] = await Promise.all([
    supabase
      .from("articles")
      .select("slug, title, featured_image")
      .eq("status", "published")
      .lt("published_at", publishedAt)
      .order("published_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from("articles")
      .select("slug, title, featured_image")
      .eq("status", "published")
      .gt("published_at", publishedAt)
      .order("published_at", { ascending: true })
      .limit(1)
      .maybeSingle(),
  ]);

  return { previous: prev || null, next: next || null };
}

/** Other published articles by the same author (by author_id or author_name), excluding the current one. */
export async function getArticlesByAuthor({ authorId, authorName, excludeId, limit = 4 }) {
  let query = supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(limit);

  if (authorId) query = query.eq("author_id", authorId);
  else if (authorName) query = query.eq("author_name", authorName);
  else return [];

  if (excludeId) query = query.neq("id", excludeId);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(normalizeArticle);
}

/** All visible categories, ordered for display in the category filter chips. */
export async function getBlogCategories() {
  const { data, error } = await supabase
    .from("categories")
    .select("id, name, slug, display_order")
    .eq("visible", true)
    .order("display_order", { ascending: true });

  if (error) throw error;
  return data || [];
}

/** All tags, alphabetically. */
export async function getBlogTags() {
  const { data, error } = await supabase
    .from("tags")
    .select("id, name, slug")
    .order("name", { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Full-text + category/tag-name search across published articles,
 * via the search_articles() Postgres function (single round trip).
 */
export async function searchArticles(term, limit = 20) {
  const query = (term || "").trim().toLocaleLowerCase();
  if (!query) return [];

  const { data: matches, error } = await supabase.rpc("search_articles", {
    search_query: query,
    result_limit: limit,
  });
  if (error) throw error;

  const ids = (matches || []).map((m) => m.id);
  if (!ids.length) return [];

  const { data, error: articlesError } = await supabase
    .from("articles")
    .select(ARTICLE_COLUMNS)
    .in("id", ids);
  if (articlesError) throw articlesError;

  const byId = new Map((data || []).map((row) => [row.id, normalizeArticle(row)]));
  // Preserve the relevance ranking returned by search_articles().
  return ids.map((id) => byId.get(id)).filter(Boolean);
}
